import axios from "axios";
import Cookies from "js-cookie";

const ACCESS_TOKEN_REFRESH_SKEW_MS = 60 * 1000;

let refreshInFlight = null;

const getJwtExpMs = (token) => {
  if (!token || typeof token !== "string") return null;

  const parts = token.split(".");

  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));

    if (typeof payload.exp !== "number") return null;

    return payload.exp * 1000;
  } catch {
    return null;
  }
};

const isAccessTokenFresh = (jwt, skewMs) => {
  const expMs = getJwtExpMs(jwt);

  if (expMs == null) return false;

  return expMs > Date.now() + skewMs;
};

const refreshTokensWithRt = () => {
  const rt = Cookies.get("refresh");

  if (!rt) {
    return Promise.reject(new Error("No refresh token"));
  }

  if (!refreshInFlight) {
    refreshInFlight = axios
      .post("/jwt", {rt}, {withCredentials: true})
      .then((response) => {
        Cookies.set("refresh", response.data.rt);
        Cookies.set("jwt", response.data.jwt);

        return response;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
};

const shouldSkipProactiveRefresh = (config) => {
  if (config.skipAccessTokenRefresh === true) return true;

  const url = String(config.url || "");

  return url.includes("/jwt") || url.includes("sign_in");
};

const ensureAccessTokenFresh = async () => {
  const rt = Cookies.get("refresh");

  if (!rt) return;

  const jwt = Cookies.get("jwt");

  if (isAccessTokenFresh(jwt, ACCESS_TOKEN_REFRESH_SKEW_MS)) return;

  try {
    await refreshTokensWithRt();
  } catch {
    // Оставляем как есть; запрос уйдёт со старым jwt → 401 → fallback в response.
  }
};

const $api = axios.create({
  withCredentials: true,
  baseURL: "",
  headers: {
    "Content-Type": "application/json",
  },
  transformRequest: [
    (data, headers) => {
      if (data instanceof FormData) {
        delete headers["Content-Type"];
        return data;
      }

      if (data && typeof data === "object") {
        headers["Content-Type"] = "application/json";
        return JSON.stringify(data);
      }

      return data;
    },
  ],
});

$api.interceptors.request.use(
  async (config) => {
    if (!shouldSkipProactiveRefresh(config)) {
      await ensureAccessTokenFresh();
    }

    const jwt = Cookies.get("jwt");

    if (jwt) {
      config.headers.Authorization = `Bearer ${jwt}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

$api.interceptors.response.use(
  (config) => {
    return config;
  },
  async (error) => {
    const originalRequest = {...error.config};
    originalRequest._isRetry = true;

    if (
      error.response?.status === 401 &&
      error.config &&
      !error.config._isRetry
    ) {
      try {
        const rt = Cookies.get("refresh");

        if (rt) {
          await refreshTokensWithRt();

          originalRequest.headers.Authorization = `Bearer ${Cookies.get("jwt")}`;

          return $api.request(originalRequest);
        } else {
          throw new Error("Error!");
        }
      } catch {
        if (!originalRequest.url.includes("sign_in")) {
          window.location.href = "/";
        }
      }
    }

    return Promise.reject(error);
  },
);

export default $api;
